import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import MachineAPI, { AddMachineAPIType, AddOtherBrandMachineAPIType } from '~/api/machine-api';
import type AssetTypeConstants from '~/constants/asset-type-constants';
import ErrorRecord from '~/data/ErrorRecord';
import type { AssetListItem } from '~/data/AssetListItem/AssetListItem';
import type {
  AssetBrandGroupsOtherType,
  AssetCategoryType,
  AssetSubcategoryType,
  HvaBrandType,
  OtherBrandModelType,
} from '~/ducks/asset-types/data/types';
import createCaughtAsyncThunk from '~/utils/create-action/create-caught-async-thunk';
import type ModalStep from '../data/modal-step';
import { defaultState } from '../data/AddMachineState';

export const addMachine = createCaughtAsyncThunk<AssetListItem, AddMachineAPIType>(
  'addMachine',
  async (args) => {
    return MachineAPI.registerMachine(args);
  }
);

export const addOtherBrandMachine = createCaughtAsyncThunk<
  AssetListItem,
  AddOtherBrandMachineAPIType
>('addOtherBrandMachine', async (args) => {
  return MachineAPI.registerOtherBrandMachine(args);
});

const slice = createSlice({
  name: 'add-machine',
  initialState: defaultState,
  reducers: {
    toggleAddMachineModalOpen: (
      state,
      action: PayloadAction<
        | undefined
        | {
            step: ModalStep;
            assetType: AssetTypeConstants;
          }
      >
    ) => {
      return {
        ...defaultState,
        step: action.payload?.step,
        assetType: action.payload?.assetType || '',
        modalIsOpen: !state.modalIsOpen,
      };
    },
    setModalStep: (state, action: PayloadAction<ModalStep>) => {
      state.step = action.payload;
      delete state.error;
    },
    setBrandGroup: (
      state,
      action: PayloadAction<null | HvaBrandType | AssetBrandGroupsOtherType>
    ) => {
      state.brandGroup = action.payload;
    },
    setModalSubcategory: (
      state,
      action: PayloadAction<{
        category: AssetCategoryType;
        subcategory: AssetSubcategoryType;
      }>
    ) => {
      state.category = action.payload.category;
      state.subcategory = action.payload.subcategory;
      delete state.error;
    },
    setModalModel: (state, action: PayloadAction<any>) => {
      state.model = action.payload;
    },
    setModalOtherBrandModel: (state, action: PayloadAction<OtherBrandModelType | null>) => {
      state.otherBrandModel = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action): action is any =>
          [addMachine.pending.type, addOtherBrandMachine.pending.type].includes(action.type),
        (state) => {
          state.isAdding = true;
          delete state.error;
        }
      )
      .addMatcher(
        (action): action is any =>
          [addMachine.fulfilled.type, addOtherBrandMachine.fulfilled.type].includes(action.type),
        (state, action) => {
          const newMachineData = {
            ...action.payload,
            isSelectable: true,
          };
          state.isAdding = false;
          state.isAdded = true;
          state.addedMachine = newMachineData as AssetListItem;
          delete state.step;
        }
      )
      .addMatcher(
        (action): action is any =>
          [addMachine.rejected.type, addOtherBrandMachine.rejected.type].includes(action.type),
        (state, action) => {
          state.isAdding = false;
          state.isAdded = false;
          state.error = new ErrorRecord(action.payload);
        }
      );
  },
});

export const {
  setBrandGroup,
  setModalModel,
  setModalOtherBrandModel,
  setModalStep,
  setModalSubcategory,
  toggleAddMachineModalOpen,
} = slice.actions;

export default slice.reducer;
